import { useCallback } from 'react';
import { DropdownItem } from 'reactstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmbulance } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { Modal } from '../../../../common/Modal';
import { ApiRoutes } from '../../../../Common';
import { toasty } from '../../../../common/forms/FormElements';
import { useJobQueryKey } from '../../common/useJobQueryKey.hook';
import { useUpdateAssignment } from './useUpdateAssignment.hook';

const defaultOnSuccess = () => { };

export const useHandleSetEmergency = ({
    job, day, onSuccess = defaultOnSuccess,
    ...mutationHandlers
}) => {
    const dayAssignments = job?.assignments?.[day];

    const handleSetEmergencySubmit = useMutation({
        ...mutationHandlers,
        mutationFn: () => {
            const eqIds = dayAssignments.equipment.map(
                (x) => x.jobAssignmentId,
            );
            const empIds = dayAssignments.staff.map(
                (x) => x.jobAssignmentId,
            );

            const ids = [...eqIds, ...empIds];
            const model = {
                jobAssignmentIds: ids,
                isEmergency: !dayAssignments.isEmergency,
            };

            return httpClient.post(
                ApiRoutes.jobAssignment.setEmergency(),
                model,
            );
        },
        onSuccess: () => {
            toasty.success('Updated job emergency status.');
            onSuccess(!dayAssignments.isEmergency);
        },
    });

    const handleSetEmergency = useCallback(() => {
        // If no emps or equipment, no need to open editor.
        if (
            !(dayAssignments.staff ?? []).length
            && !(dayAssignments.equipment ?? []).length
        ) {
            toasty.error(
                'Please make an assignment to the job on this day before setting an emergency.',
            );
            return false;
        }

        // Need at least one emp to make status edits. `assignment` is the true signal that an
        // employee is actually assigned.
        if (
            !(dayAssignments.staff ?? []).filter((x) => x.assignment)
                .length
        ) {
            toasty.error(
                'Please assign at least one employee to the job on this day before setting an emergency.',
            );
            return false;
        }

        // Make sure no equipment is unassigned.
        if (
            (dayAssignments.equipment ?? []).filter(
                (x) => !x.assignedEquipment?.id && !x.assignedEmployee?.id,
            ).length
        ) {
            toasty.error(
                'Please ensure all job equipment has been assigned on this day before setting an emergency.',
            );
            return false;
        }

        Modal.show({
            title: 'Emergency Job Day',
            SaveButtonProps: {
                label: 'Emergency',
                savingLabel: 'Please Wait...',
                color: 'primary',
            },
            onSubmit: handleSetEmergencySubmit.mutateAsync,
            body: dayAssignments.isEmergency === false
                ? 'Are you sure you would like to set this job as emergency work?'
                : 'Are you sure you would like to remove emergency work from this job?',
        });
    }, [dayAssignments, handleSetEmergencySubmit.mutateAsync]);

    return handleSetEmergency;
};

export const SetEmergency = ({ day, ...job }) => {
    const { queryKey } = useJobQueryKey();

    const queryClient = useQueryClient();
    const handleMutate = useUpdateAssignment({
        jobId: job.id,
        jobAssignmentId: null,
        onMutateGroup: (resource, isEmergency) => ({
            ...resource,
            isEmergency,
        }),
    });

    const handleSuccess = useCallback((isEmergency) => {
        handleMutate.onMutate(isEmergency);
        queryClient.invalidateQueries({ queryKey });
    }, [queryClient, queryKey, handleMutate]);

    const handleSetEmergency = useHandleSetEmergency({
        job, day, ...handleMutate, onSuccess: handleSuccess,
    });

    return (
        <DropdownItem
            className="text-danger"
            onClick={handleSetEmergency}
        >
            <FontAwesomeIcon icon={faAmbulance} />
            <span>Emergency</span>
        </DropdownItem>
    );
};

